import React, { ReactNode } from "react";
import { makeStyles } from "@material-ui/core";
import { TextSize, TextGrayScale } from "../../models/element";
import classNames from "classnames";
import { NEW_LINE } from "../../utils/Contants";
import { isNullorUndefined } from "../../utils/ObjectUtils";

interface TextStyleProps {
    // 文字サイズ
    size?: TextSize;
    // 文字太さ
    bold?: boolean;
    // 文字色
    color?: TextGrayScale;
}

interface TextProps extends TextStyleProps {
    // 要素ID
    id?: string;
    // 文字列
    children: ReactNode;
    // 行数
    rows?: number;
}

const useStyles = makeStyles({
    text10: { fontSize: "10px", lineHeight: "16px" },
    text12: { fontSize: "12px", lineHeight: "18px" },
    text14: { fontSize: "14px", lineHeight: "20px" },
    text16: { fontSize: "16px", lineHeight: "24px" },
    text18: { fontSize: "18px", lineHeight: "30px" },
    text20: { fontSize: "20px", lineHeight: "28px" },
    text24: { fontSize: "24px", lineHeight: "32px" },
    text30: { fontSize: "30px", lineHeight: "38px" },
    text40: { fontSize: "40px", lineHeight: "48px" },
    mainTitle: { fontSize: "20px" },
    videoTitle: { fontSize: "15px" },
    normal: { fontWeight: "normal" },
    bold: { fontWeight: "bold" },
    black: { color: "black" },
    gray42: { color: "var(--gray-42)" },
    gray61: { color: "var(--gray-61)" },
    gray80: { color: "var(--gray-80)" },
    gray85: { color: "var(--gray-85)" },
    gray97: { color: "var(--gray-97)" },
    primaryBg: { color: "var(--primary-bg)" },
    secondaryBg: { color: "var(--secondary-bg)" },
    primaryFont: { color: "var(--primary-font)" },
    secondaryFont: { color: "var(--loading-bg)" },
    adminEditPageBg: { color: "var(--adminEditPage-bg)" },
    white: { color: "white" },
    red: { color: "var(--red)" },
    singleRow: { overflow: "hidden", whiteSpace: "nowrap", textOverflow: "ellipsis" },
    multiRow: { overflow: "hidden", textOverflow: "ellipsis" },
});

function PuiTypography({ id, children, rows = 0, ...props }: TextProps) {
    const { size = 16, bold = false, color = "white" } = props;
    const classes = useStyles();

    function getSizeStyle(size?: TextSize) {
        switch (size) {
            case 10:
                return classes.text10;
            case 12:
                return classes.text12;
            case 14:
                return classes.text14;
            case 16:
                return classes.text16;
            case 18:
                return classes.text18;
            case 20:
                return classes.text20;
            case 24:
                return classes.text24;
            case 30:
                return classes.text30;
            case 40:
                return classes.text40;
            case "mainTitle":
                return classes.mainTitle;
            case "videoTitle":
                return classes.videoTitle;
            default:
                return classes.text16;
        }
    }

    function getWeightStyle(bold?: boolean) {
        return bold === true ? classes.bold : classes.normal;
    }

    function getColorStyle(color?: TextGrayScale) {
        switch (color) {
            case "black":
                return classes.black;
            case "gray-42":
                return classes.gray42;
            case "gray-61":
                return classes.gray61;
            case "gray-80":
                return classes.gray80;
            case "gray-85":
                return classes.gray85;
            case "gray-97":
                return classes.gray97;
            case "primary-font":
                return classes.primaryFont;
            case "secondary-font":
                return classes.secondaryFont;
            case "white":
                return classes.white;
            case "red":
                return classes.red;
            default:
                return classes.black;
        }
    }

    function getRowStyle(rows?: number) {
        return rows && rows <= 1 ? classes.singleRow : classes.multiRow;
    }

    return (
        <>
            {String(children).includes(NEW_LINE) &&
                String(children)
                    .split(NEW_LINE)
                    .map((item, index) => (
                        <span
                            id={id}
                            key={index}
                            className={classNames(
                                getRowStyle(rows),
                                getSizeStyle(size),
                                getWeightStyle(bold),
                                getColorStyle(color)
                            )}
                        >
                            {item}
                            <br />
                        </span>
                    ))}
            {!isNullorUndefined(children) && !String(children).includes(NEW_LINE) && (
                <span
                    id={id}
                    className={classNames(
                        getRowStyle(rows),
                        getSizeStyle(size),
                        getWeightStyle(bold),
                        getColorStyle(color)
                    )}
                >
                    {children}
                </span>
            )}
        </>
    );
}

export default PuiTypography;
